import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuList,
  type PopoverOrigin,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import cn from 'classnames';
import DetectRTC from 'detectrtc';
import React, { Fragment, useEffect } from 'react';
import { PopoverProps } from 'src/components/PopoverTrigger';
import Iconify from 'src/components/iconify';
import { voiceJoinLeave } from 'src/features/audio/sounds';
import { useLocales } from 'src/locales';
import { VideoFilterType } from 'src/provider/VoiceChannel/livekit/types';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import useUIState from 'src/state/ui/persistUiState';
import { MicDeviceMenuItem } from '../MicDeviceMenuItem';
import { ToggleAudioNoiseFilter } from './AudioNoiseFilter';
import { EffectsBlurEdit } from './EditEffects/EffectsBlurEdit';
import { EffectsVirtualBackgroundEdit } from './EditEffects/EffectsVirtualBackgroundEdit';
import { VideoCameraInput } from './VideoCameraInput/VideoCameraInput';
import { InputOutputSection, VideoEffectsOptionsProps } from './types';

const videoEffectsOptions: VideoEffectsOptionsProps[] = [
  {
    label: 'No Filter',
    translate: 'shared.effects.no_filter',
    value: null,
  },
  {
    label: 'Blur Background',
    translate: 'shared.effects.blur_background',
    value: 'blur',
    editComponent: EffectsBlurEdit,
  },
  {
    label: 'Image Background',
    translate: 'shared.effects.image_background',
    value: 'image',
    editComponent: EffectsVirtualBackgroundEdit,
  },
];

type PopoverContextProps = {
  showSections?: InputOutputSection[];
  isConference?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
};

export const InputOutputSelector = ({
  target,
  handleClose,
  showSections = [
    InputOutputSection.CAMERA,
    InputOutputSection.MICROPHONE,
    InputOutputSection.SPEAKER,
  ],
  isConference,
  anchorOrigin = { vertical: 'top', horizontal: 'left' },
  transformOrigin = { vertical: 'bottom', horizontal: 'left' },
}: PopoverProps<PopoverContextProps>) => {
  const open = Boolean(target);

  const { defaultDevice, changeDevice, videoFilter, setVideoFilter, room } =
    useLivekitRTCState();
  const { translate } = useLocales();

  const { setVideoFilter: setVideoFilterUIState } = useUIState();

  useEffect(() => {
    if (
      !showSections?.includes(InputOutputSection.CAMERA) ||
      !DetectRTC.hasWebcam
    )
      return;
    const selectedVideo = DetectRTC.videoInputDevices.find(
      (video) => video.id === defaultDevice.video,
    );
    if (!selectedVideo) {
      changeDevice('videoinput', DetectRTC.videoInputDevices[0].id);
    }
  }, [changeDevice, defaultDevice.video, showSections]);

  const handleTestDevice = (
    deviceId: string,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    const audio = new Audio(voiceJoinLeave);
    // @ts-ignore
    audio.setSinkId(deviceId).then(() => {
      audio.play();
    });
  };

  const handleTestVideoFilter = (videoFilter: VideoFilterType | null) => {
    setVideoFilter(videoFilter);
    setVideoFilterUIState(videoFilter);
  };

  return (
    <StyledMenu
      open={open}
      anchorEl={target}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      {showSections.includes(InputOutputSection.CAMERA) && (
        <MenuList>
          <StyledHeader>
            <Iconify
              icon="tabler:video-filled"
              color={(theme) => theme.palette.text.secondary}
            />
            <Typography variant="subtitle1">
              {translate('shared.labels.camera')}
            </Typography>
          </StyledHeader>
          {!DetectRTC.hasWebcam && (
            <StyledNoDevicesContainer>
              <Typography
                variant="caption"
                color={(theme) => theme.palette.text.secondary}
              >
                {translate('communicate.rtc_controls.no_video_input_devices')}
              </Typography>
            </StyledNoDevicesContainer>
          )}

          {DetectRTC.hasWebcam && !room?.localParticipant.isCameraEnabled && (
            <VideoCameraInput
              deviceId={defaultDevice.video}
              style={{ margin: 8 }}
            />
          )}

          {DetectRTC.videoInputDevices.map((video) => (
            <MenuItem
              key={video.id}
              sx={{ justifyContent: 'space-between', gap: 2 }}
              onClick={() => {
                changeDevice('videoinput', video.id);
              }}
            >
              <ListItemIcon sx={{ padding: 1 }}>
                <Iconify
                  icon="tabler:check"
                  color={(theme) =>
                    video.id === defaultDevice.video
                      ? theme.palette.success.main
                      : 'transparent'
                  }
                />
              </ListItemIcon>
              <StyledTextItem variant="caption">{video.label}</StyledTextItem>
            </MenuItem>
          ))}

          {!isConference && (
            <Fragment>
              <StyledHeader className="border-top">
                <Iconify
                  icon="tabler:video-filled"
                  color={(theme) => theme.palette.text.secondary}
                />
                <Typography variant="subtitle1">
                  {translate('communicate.rtc_controls.camera_effects')}
                </Typography>
              </StyledHeader>

              {videoEffectsOptions.map((effect) => (
                <MenuItem
                  key={effect.value}
                  sx={{ justifyContent: 'space-between', gap: 2 }}
                  onClick={() => {
                    handleTestVideoFilter(effect.value);
                  }}
                >
                  <Stack direction="row" alignItems="center" overflow="hidden">
                    <ListItemIcon sx={{ padding: 1 }}>
                      <Iconify
                        icon="tabler:check"
                        color={(theme) =>
                          effect.value === videoFilter
                            ? theme.palette.success.main
                            : 'transparent'
                        }
                      />
                    </ListItemIcon>
                    <StyledTextItem variant="caption">
                      {translate(effect.translate)}
                    </StyledTextItem>
                  </Stack>
                  {!!effect.editComponent && <effect.editComponent />}
                </MenuItem>
              ))}
            </Fragment>
          )}
        </MenuList>
      )}

      {showSections.includes(InputOutputSection.MICROPHONE) && (
        <MenuList>
          <StyledHeader
            className={cn({
              'border-top': showSections.includes(InputOutputSection.CAMERA),
            })}
            sx={{ justifyContent: 'space-between' }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Iconify
                icon="tabler:microphone-filled"
                color={(theme) => theme.palette.text.secondary}
              />
              <Typography variant="subtitle1">
                {translate('shared.labels.microphones')}
              </Typography>
            </Stack>
            <ToggleAudioNoiseFilter />
          </StyledHeader>
          {!DetectRTC.hasMicrophone && (
            <StyledNoDevicesContainer>
              <Typography
                variant="caption"
                color={(theme) => theme.palette.text.secondary}
              >
                {translate('communicate.rtc_controls.no_input_devices')}
              </Typography>
            </StyledNoDevicesContainer>
          )}

          {DetectRTC.audioInputDevices.map((input) => (
            <MicDeviceMenuItem
              key={input.id}
              icon="tabler:check"
              device={input}
              selected={input.id === defaultDevice.input}
              onClick={() => {
                changeDevice('audioinput', input.id);
              }}
            />
          ))}
        </MenuList>
      )}

      {showSections.includes(InputOutputSection.SPEAKER) && (
        <MenuList>
          <StyledHeader
            className={cn({ 'border-top': showSections?.length > 1 })}
          >
            <Iconify
              icon="tabler:headphones-filled"
              color={(theme) => theme.palette.text.secondary}
            />
            <Typography variant="subtitle1">
              {translate('shared.labels.speaker')}
            </Typography>
          </StyledHeader>

          {!DetectRTC.hasSpeakers && (
            <StyledNoDevicesContainer>
              <Typography
                variant="caption"
                color={(theme) => theme.palette.text.secondary}
              >
                {translate('communicate.rtc_controls.no_output_devices')}
              </Typography>
            </StyledNoDevicesContainer>
          )}

          {DetectRTC.audioOutputDevices.map((output) => (
            <MenuItem
              key={output.id}
              sx={{ justifyContent: 'space-between', gap: 2 }}
              onClick={() => {
                changeDevice('audiooutput', output.id);
              }}
            >
              <Stack direction="row" alignItems="center" overflow="hidden">
                <ListItemIcon>
                  {output.id === defaultDevice.output && (
                    <Iconify
                      icon="tabler:check"
                      color={(theme) => theme.palette.success.main}
                    />
                  )}
                </ListItemIcon>
                <StyledTextItem variant="caption">
                  {output.label}
                </StyledTextItem>
              </Stack>
              <IconButton onClick={(e) => handleTestDevice(output.deviceId, e)}>
                <Iconify icon="tabler:player-play-filled" />
              </IconButton>
            </MenuItem>
          ))}
        </MenuList>
      )}
    </StyledMenu>
  );
};

const StyledHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0, 1, 1),
  '&.border-top': {
    paddingTop: theme.spacing(4),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
}));

const StyledNoDevicesContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
}));

const StyledTextItem = styled(Typography)(({ theme }) => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiMenuItem-root': {
    padding: theme.spacing(0.75, 1),
    '&:not(:last-of-type)': {
      marginBottom: 0,
    },
  },
}));
